import { ICharacter, ICharacterMetadata } from "@model";

export const CHARACTER_LANGUAGES = [
  { key: "en", label: "English", voiceCode: "en-us" },
  { key: "es", label: "Spanish", voiceCode: "es-es" },
  { key: "fr", label: "French", voiceCode: "fr-fr" },
  { key: "de", label: "German", voiceCode: "de-de" },
  { key: "it", label: "Italian", voiceCode: "it-it" },
  { key: "tr", label: "Turkish", voiceCode: "tr-tr" },
  { key: "ja", label: "Japanese", voiceCode: "ja-jp" },
  { key: "ko", label: "Korean", voiceCode: "ko-kr" },
  { key: "zh", label: "Chinese", voiceCode: "zh-cn" },
] as const;

/** Origin and app languages. Persian is not a learning target. */
export const ORIGIN_LANGUAGES = [
  { key: "en", label: "English", voiceCode: "en-us" },
  { key: "es", label: "Spanish", voiceCode: "es-es" },
  { key: "fr", label: "French", voiceCode: "fr-fr" },
  { key: "de", label: "German", voiceCode: "de-de" },
  { key: "it", label: "Italian", voiceCode: "it-it" },
  { key: "tr", label: "Turkish", voiceCode: "tr-tr" },
  { key: "fa", label: "Persian", voiceCode: "fa-ir" },
  { key: "ja", label: "Japanese", voiceCode: "ja-jp" },
  { key: "ko", label: "Korean", voiceCode: "ko-kr" },
  { key: "zh", label: "Chinese", voiceCode: "zh-cn" },
] as const;

export const TARGET_LANGUAGE_OPTIONS = CHARACTER_LANGUAGES.map((lang) => ({
  value: lang.voiceCode,
  label: lang.label,
}));

export type CharacterLanguageKey = (typeof CHARACTER_LANGUAGES)[number]["key"];

export const CHARACTER_LANGUAGE_OPTIONS = CHARACTER_LANGUAGES.map((lang) => ({
  value: lang.key,
  label: lang.label,
}));

export function talkSubjectsToFormValue(
  value?: string[] | string | null,
): string {
  if (Array.isArray(value)) {
    return value.map((s) => String(s ?? "").trim()).filter(Boolean).join("\n");
  }
  if (typeof value === "string") return value;
  return "";
}

export function talkSubjectsFromFormValue(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s ?? "").trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function hasMetadataContent(value?: {
  name?: string;
  voice?: string;
  description?: string;
  starterText?: string;
  talkSubjects?: string[] | string;
}): boolean {
  if (!value) return false;
  return Boolean(
    value.name?.trim() ||
      value.voice?.trim() ||
      value.description?.trim() ||
      value.starterText?.trim() ||
      talkSubjectsFromFormValue(value.talkSubjects).length,
  );
}

export function normalizeCharacterMetadata(
  metadata?: ICharacterMetadata | null,
): ICharacterMetadata {
  if (!metadata || typeof metadata !== "object") return {};
  return Object.fromEntries(
    Object.entries(metadata)
      .filter(([, value]) => value && typeof value === "object")
      .map(([key, value]) => [
        key,
        {
          ...value,
          talkSubjects: talkSubjectsToFormValue(value.talkSubjects),
        },
      ]),
  );
}

export function migrateCharacterMetadataFromLegacy(
  character: Pick<
    ICharacter,
    "description" | "starterText" | "talkSubjects" | "metadata"
  >,
): ICharacterMetadata {
  const metadata = normalizeCharacterMetadata(character.metadata);
  const en = { ...(metadata.en ?? {}) };

  if (!en.description?.trim() && character.description?.trim()) {
    en.description = character.description.trim();
  }
  if (!en.starterText?.trim() && character.starterText?.trim()) {
    en.starterText = character.starterText.trim();
  }
  if (
    !talkSubjectsFromFormValue(en.talkSubjects).length &&
    character.talkSubjects?.length
  ) {
    en.talkSubjects = talkSubjectsToFormValue(character.talkSubjects);
  }

  if (hasMetadataContent(en)) {
    metadata.en = en;
  }

  return metadata;
}

export function cleanCharacterMetadata(
  metadata?: ICharacterMetadata | null,
): ICharacterMetadata {
  const normalized = normalizeCharacterMetadata(metadata);
  return Object.fromEntries(
    Object.entries(normalized)
      .filter(([, value]) => hasMetadataContent(value))
      .map(([key, value]) => {
        const talkSubjects = talkSubjectsFromFormValue(value.talkSubjects);
        return [
          key,
          {
            ...(value?.name?.trim() ? { name: value.name.trim() } : {}),
            ...(value?.voice?.trim() ? { voice: value.voice.trim() } : {}),
            ...(value?.description?.trim()
              ? { description: value.description.trim() }
              : {}),
            ...(value?.starterText?.trim()
              ? { starterText: value.starterText.trim() }
              : {}),
            ...(talkSubjects.length ? { talkSubjects } : {}),
          },
        ];
      }),
  );
}

export function getConfiguredLanguageKeys(
  metadata?: ICharacterMetadata | null,
): string[] {
  return Object.keys(cleanCharacterMetadata(metadata));
}
